import { collection, getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore'
import type { QueryConstraint, QueryDocumentSnapshot } from 'firebase/firestore'
import { ref } from 'vue'
import { AUDIT_LOG_COLLECTION } from '~/shared/utils/auditLog.js'
import type { AuditEntry } from '~/shared/utils/auditLog.js'

export interface AuditLogRow {
  id: string
  entry: AuditEntry
}

export type AuditLogFilter = { refPath: string } | { actorUid: string }

function toDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') return (value as { toDate: () => Date }).toDate()
  return new Date(Number.NaN)
}

/**
 * Page through `audit_log`, newest first: the history of one document
 * (`refPath`, e.g. 'subkrewes/abc') or everything one member did (`actorUid`).
 * One-shot reads rather than a listener — history is looked at, not watched.
 * Readable by full admins only (firestore.rules).
 */
export function useAuditLog(filter: AuditLogFilter, pageSize = 25) {
  const db = useFirestore()
  const rows = ref<AuditLogRow[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const hasMore = ref(false)
  let cursor: QueryDocumentSnapshot | null = null

  const fetchPage = async (reset: boolean) => {
    if (loading.value) return
    loading.value = true
    error.value = null
    try {
      const scope = 'refPath' in filter
        ? where('refs', 'array-contains', filter.refPath)
        : where('actor.uid', '==', filter.actorUid)
      const constraints: QueryConstraint[] = [scope, orderBy('at', 'desc'), limit(pageSize)]
      if (!reset && cursor) constraints.push(startAfter(cursor))

      const snap = await getDocs(query(collection(db, AUDIT_LOG_COLLECTION), ...constraints))
      const page = snap.docs.map((doc) => {
        const data = doc.data() as AuditEntry
        return { id: doc.id, entry: { ...data, at: toDate(data.at), expireAt: toDate(data.expireAt) } as AuditEntry }
      })

      rows.value = reset ? page : [...rows.value, ...page]
      cursor = snap.docs[snap.docs.length - 1] ?? cursor
      hasMore.value = snap.docs.length === pageSize
    }
    catch (err) {
      console.error('[useAuditLog] failed to load history', err)
      error.value = 'Could not load the history.'
    }
    finally {
      loading.value = false
    }
  }

  return {
    rows,
    loading,
    error,
    hasMore,
    load: () => fetchPage(true),
    loadMore: () => fetchPage(false),
  }
}
